'use memo';

import { forwardRef, useId, useState, type ChangeEvent, type FocusEvent, type ReactNode } from 'react';
import IconButton from '@/components/Button/IconButton';
import Input from '@/components/Input/Input';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Label } from '@/components/ui/label';
import { areAllPasswordRequirementsMet, evaluatePasswordRequirements, type PasswordPolicyRequirements } from '@/lib/api/orpc/contracts/auth-password';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { PasswordRequirementList } from './PasswordRequirementList';
export type PasswordFieldProps = {
  id?: string;
  /** 空文字または未指定ならラベルを描画しない（外側にラベルがある場合用） */
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  autoComplete?: string;
  placeholder?: string;
  name?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  /** 未指定なら要件UIなし（current-password 用途） */
  policy?: PasswordPolicyRequirements;
  showVisibilityToggle?: boolean;
  /** Input の追加 className */
  className?: string;
  /** ラベル直下・入力上に差し込む補助（任意） */
  description?: ReactNode;
};
export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(function PasswordField({
  id: idProp,
  label,
  value,
  onChange,
  onBlur,
  disabled = false,
  autoComplete = 'new-password',
  placeholder,
  name,
  required,
  error = false,
  errorMessage,
  policy,
  showVisibilityToggle = true,
  className,
  description
}, ref) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.passwordField'
  });
  const generatedId = useId();
  const inputId = idProp ?? generatedId;
  const [visible, setVisible] = useState(false);
  const [touched, setTouched] = useState(false);
  const requirements = policy != null ? evaluatePasswordRequirements(value, policy) : [];
  const hasInput = value.length > 0;
  const allMet = policy != null && areAllPasswordRequirementsMet(value, policy);
  const emphasizeUnmet = touched && hasInput && !allMet;
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };
  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    onBlur?.(event);
  };
  return <VStack className="w-full gap-1.5">
        {label != null && label !== '' && <Label htmlFor={inputId}>{label}</Label>}
        {description}
        <Input ref={ref} id={inputId} name={name} type={visible ? 'text' : 'password'} width="100%" autoComplete={autoComplete} value={value} disabled={disabled} required={required} placeholder={placeholder} error={error} className={className} onChange={handleChange} onBlur={handleBlur} suffix={showVisibilityToggle ? <IconButton type="button" variant="ghost" size="ExtraSmall" icon={visible ? 'EyeOff' : 'Eye'} aria-label={visible ? t('hidePassword') : t('showPassword')} aria-pressed={visible} disabled={disabled} onClick={() => setVisible(prev => !prev)} /> : undefined} />
        {errorMessage != null && errorMessage !== '' && <div role="alert">
            <Text variant="labelSmallReg" className="text-error-on-neutral-fill">
              {errorMessage}
            </Text>
          </div>}
        {policy != null && <PasswordRequirementList requirements={requirements} minLength={policy.minLength} hasInput={hasInput} allMet={allMet} emphasizeUnmet={emphasizeUnmet} />}
      </VStack>;
});